import { Metamodel } from '../base/model';
import { format } from '../../shared/utils/string.util';
import { ModelConstants } from '../model-constants';
import { shellStringResources } from '../../Resources/ShellStringResources';
import { AzureSqlDWLinkedService } from './azure-sql-dw.linkedservice';
import { IAttribute, IValidationContext, IViolation } from '../base/annotation';
import { Credential } from '../credential/credential';
import { GlobalVariables } from '../../constants/globals';
import { AzureKeyVault } from './key-vault';
import { azureDefaultAuthRefTypes, warehouseAuthTypes } from '../../interfaces/IAuthenticationType';
import {
    validateAKVLinkedServiceReference,
    validateLinkedServiceProperties
} from '../../shared/utils/linkedservice-validation-util';
import { SqlVersionType } from '../../constants/sql-constants';
import { Named } from '../base/named';

@Metamodel.model({
    name: GlobalVariables.isTrident ? ModelConstants.dataWarehouseLinkedService : ModelConstants.warehouseLinkedService,
    display: [
        format(shellStringResources.ModelName_LinkedServiceTemplate, shellStringResources.Warehouse_DisplayText),
        format(shellStringResources.ModelName_LinkedServicesTemplate, shellStringResources.Warehouse_DisplayText)
    ],
    validator: DataWarehouseLinkedService.validateDataWarehouseLinkedService
})
export class DataWarehouseLinkedService extends AzureSqlDWLinkedService {
    public static ARTIFACT_ID: IAttribute;
    public static ENDPOINT: IAttribute;
    public static WORKSPACE_ID: IAttribute;

    public static async validateDataWarehouseLinkedService(
        linkedService: DataWarehouseLinkedService,
        context: IValidationContext
    ): Promise<IViolation[]> {
        if (GlobalVariables.isTrident) {
            // MDF: Workspace built-in artifacts use objectId, others use artifactId/endpoint
            if (GlobalVariables.isTridentMdf && linkedService.isWorkspaceBuiltIn) {
                // Custom validation with proper display name for Warehouse
                if (!linkedService.objectId) {
                    return [{
                        message: () => format(shellStringResources.RequiredTemplate, shellStringResources.Warehouse_Fabric_DisplayText),
                        redirectTo: {
                            entity: linkedService.owner as Named,
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            attribute: { name: 'mdfArtifact' } as any
                        },
                        entity: linkedService,
                        // Revalidate checks the CURRENT linked service from owner, not the captured one.
                        // This is needed because the linked service is replaced (not modified) when user selects an artifact,
                        // so the old validator's corrected$ would keep checking the old linkedService object.
                        revalidate: () => {
                            const owner = linkedService.owner?.named;
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            const currentLS = (owner as any)?.sourceLinkedService || (owner as any)?.sinkLinkedService;
                            return !!currentLS?.objectId;
                        }
                    }];
                }
                return [];
            }
            return validateLinkedServiceProperties(linkedService, [
                DataWarehouseLinkedService.ARTIFACT_ID,
                DataWarehouseLinkedService.ENDPOINT
            ]);
        }

        return undefined;
    }

    public override readonly tridentArtifactType = GlobalVariables.isTrident
        ? ModelConstants.dataWarehouseArtifactType
        : undefined;

    public authenticationMethod?: string = warehouseAuthTypes.ServicePrincipalAuthentication;
    public authReferenceMethod?: string = azureDefaultAuthRefTypes.InlineRefTypeAuthentication;

    @Metamodel.attribute({
        display: format(shellStringResources.ObjectIdLabel, shellStringResources.Warehouse_DisplayText),
        templatable: true,
        association: 'ARTIFACT_ID'
    })
    public artifactId: string;

    @Metamodel.attribute({
        display: GlobalVariables.isTrident
            ? shellStringResources.SQLConnectionString
            : shellStringResources.ModelName_Endpoint,
        templatable: true,
        association: 'ENDPOINT'
    })
    public endpoint: string;

    //#region: properties in adf/synapse only
    @Metamodel.attribute({
        display: shellStringResources.WorkspaceId,
        association: 'WORKSPACE_ID',
        templatable: true
    })
    public workspaceId?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalCredentialType
    })
    public servicePrincipalCredentialType?: string;

    @Metamodel.attribute({
        display: shellStringResources.ModelName_ServicePrincipalCert,
        constraint: {
            validator: validateAKVLinkedServiceReference
        }
    })
    public servicePrincipalCert?: AzureKeyVault;

    @Metamodel.attribute({
        display: shellStringResources.Credential,
        reference: true,
        constraint: {
            validator: (
                linkedservice: DataWarehouseLinkedService,
                referencedCredential: Credential
            ): IViolation[] | undefined => {
                if (
                    linkedservice &&
                    !linkedservice.fullyByJsonEditor &&
                    ((GlobalVariables.enableSamiSupport &&
                        linkedservice.authenticationMethod === warehouseAuthTypes.MSIAuthentication) ||
                        linkedservice.authenticationMethod === warehouseAuthTypes.UserAssignedManagedIdentity ||
                        (linkedservice.authenticationMethod === warehouseAuthTypes.ServicePrincipalAuthentication &&
                            linkedservice.authReferenceMethod ===
                                azureDefaultAuthRefTypes.CredentialRefTypeAuthentication)) &&
                    !referencedCredential &&
                    !linkedservice.isWorkspaceBuiltIn
                ) {
                    return [{ message: () => shellStringResources.Error_InvalidCredentialReferenced }];
                }
                return undefined;
            }
        }
    })
    public credential?: Credential;

    public sqlVersion: SqlVersionType = 'v2';
    public sqlAnalyticsEndpoint: string;
    //#endregion
}
